/**
 * UAT 2026-05-13 F-D4 — TanStack Query hooks for monthly overage approvals.
 *
 * Cache key: ['projectMonthlyOverageApprovals', projectId]. Mutations
 * invalidate the matching list query on success.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listMonthlyOverageApprovals,
  upsertMonthlyOverageApproval,
  updateMonthlyOverageApproval,
  type UpsertMonthlyOverageApprovalInput,
  type UpdateMonthlyOverageApprovalInput,
} from '../services/api/projectMonthlyOverageApprovals';

const queryKey = (projectId: number | undefined) =>
  ['projectMonthlyOverageApprovals', projectId] as const;

/**
 * An approval save changes what the billing gate accepts, so every view that
 * derives from it must refresh: the approvals list, the uninvoiced-hours card
 * (approval chip), the package hero card and both To Be Invoiced surfaces.
 */
function invalidateApprovalDependents(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: number | undefined,
) {
  queryClient.invalidateQueries({ queryKey: queryKey(projectId) });
  queryClient.invalidateQueries({ queryKey: ['projectUninvoicedHours', projectId] });
  queryClient.invalidateQueries({ queryKey: ['projectPackageStatus', projectId] });
  queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced'] });
  queryClient.invalidateQueries({ queryKey: ['toBeInvoicedQueue'] });
}

export function useProjectMonthlyOverageApprovals(projectId: number | undefined) {
  return useQuery({
    queryKey: queryKey(projectId),
    queryFn: () => listMonthlyOverageApprovals(projectId!),
    enabled: typeof projectId === 'number' && projectId > 0,
    staleTime: 30_000,
  });
}

export function useUpsertProjectMonthlyOverageApproval(projectId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertMonthlyOverageApprovalInput) =>
      upsertMonthlyOverageApproval(projectId!, input),
    onSuccess: () => {
      invalidateApprovalDependents(queryClient, projectId);
    },
  });
}

export function useUpdateProjectMonthlyOverageApproval(projectId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      approvalId,
      input,
    }: {
      approvalId: number;
      input: UpdateMonthlyOverageApprovalInput;
    }) => updateMonthlyOverageApproval(projectId!, approvalId, input),
    onSuccess: () => {
      invalidateApprovalDependents(queryClient, projectId);
    },
  });
}
